var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Text } from '@ui/atoms/Text';
import { translate } from '@utils/i18n';
import { getFolder } from './services/CustomerFileDropService';
import { FolderList } from './components/FolderList';
import { FileList } from './components/FileList';
import { AllowlistPanel } from './components/AllowlistPanel';
import './CustomerFileDropStyles.scss';
const namespace = 'features.settings.customer_file_drop';
const FOLDER_HASH_PREFIX = 'folder/';
function parseFolderIdFromHash(hash) {
    const raw = hash.replace(/^#/, '');
    if (!raw.startsWith(FOLDER_HASH_PREFIX))
        return null;
    const id = Number(raw.slice(FOLDER_HASH_PREFIX.length));
    return Number.isFinite(id) && id > 0 ? id : null;
}
function folderHash(folderId) {
    return `#${FOLDER_HASH_PREFIX}${folderId}`;
}
function CustomerFileDrop({ canManageAllowlist, canUseDrop, canLockFolders, allowedUserIds: initialAllowedUserIds }) {
    const [selectedFolder, setSelectedFolder] = useState(null);
    const [allowedUserIds, setAllowedUserIds] = useState(initialAllowedUserIds);
    const [hashLoadError, setHashLoadError] = useState(null);
    const [isResolvingHash, setIsResolvingHash] = useState(() => parseFolderIdFromHash(window.location.hash) != null);
    const selectedFolderIdRef = useRef(null);
    const hashRequestRef = useRef(0);
    useEffect(() => {
        var _a;
        selectedFolderIdRef.current = (_a = selectedFolder === null || selectedFolder === void 0 ? void 0 : selectedFolder.id) !== null && _a !== void 0 ? _a : null;
    }, [selectedFolder]);
    const openFolder = useCallback((folder) => {
        setSelectedFolder(folder);
        setHashLoadError(null);
        setIsResolvingHash(false);
        if (window.location.hash !== folderHash(folder.id)) {
            window.location.hash = folderHash(folder.id);
        }
    }, []);
    const closeFolder = useCallback(() => {
        setSelectedFolder(null);
        setHashLoadError(null);
        if (window.location.hash) {
            const { pathname, search } = window.location;
            window.history.pushState(null, '', `${pathname}${search}`);
        }
    }, []);
    const resolveHashFolder = useCallback((folderId) => __awaiter(this, void 0, void 0, function* () {
        if (selectedFolderIdRef.current === folderId) {
            setIsResolvingHash(false);
            return;
        }
        const requestId = hashRequestRef.current + 1;
        hashRequestRef.current = requestId;
        setIsResolvingHash(true);
        setHashLoadError(null);
        try {
            const folder = yield getFolder(folderId);
            if (hashRequestRef.current !== requestId)
                return;
            if (parseFolderIdFromHash(window.location.hash) !== folderId)
                return;
            setSelectedFolder(folder);
        }
        catch (_a) {
            if (hashRequestRef.current !== requestId)
                return;
            setSelectedFolder(null);
            setHashLoadError(translate('folder_not_found', { namespace }));
            const { pathname, search } = window.location;
            window.history.replaceState(null, '', `${pathname}${search}`);
        }
        finally {
            if (hashRequestRef.current === requestId) {
                setIsResolvingHash(false);
            }
        }
    }), []);
    useEffect(() => {
        const syncFromHash = () => {
            const folderId = parseFolderIdFromHash(window.location.hash);
            if (folderId == null) {
                setSelectedFolder(null);
                setIsResolvingHash(false);
                return;
            }
            void resolveHashFolder(folderId);
        };
        syncFromHash();
        window.addEventListener('hashchange', syncFromHash);
        return () => window.removeEventListener('hashchange', syncFromHash);
    }, [resolveHashFolder]);
    if (!canUseDrop && !canManageAllowlist) {
        return (React.createElement("div", { className: "customer-file-drop customer-file-drop--denied" },
            React.createElement(Text, { variant: "body" }, translate('access_denied', { namespace }))));
    }
    return (React.createElement("div", { className: "customer-file-drop", "data-testid": "file-drop-page" },
        React.createElement("header", { className: "customer-file-drop__header" },
            React.createElement(Text, { variant: "x-large", as: "h1" }, translate('page_heading', { namespace })),
            canManageAllowlist && (React.createElement(AllowlistPanel, { allowedUserIds: allowedUserIds, onSaved: setAllowedUserIds }))),
        React.createElement("main", { className: "customer-file-drop__main" },
            hashLoadError ? React.createElement(Text, { variant: "body" }, hashLoadError) : null,
            isResolvingHash ? (React.createElement(Text, { variant: "body" }, translate('loading_folder', { namespace }))) : selectedFolder ? (React.createElement(FileList, { folder: selectedFolder, canUseDrop: canUseDrop, canLockFolders: canLockFolders, onBack: closeFolder, onFolderUpdated: setSelectedFolder })) : (React.createElement(FolderList, { canUseDrop: canUseDrop, canLockFolders: canLockFolders, onSelectFolder: openFolder })))));
}
export default CustomerFileDrop;
